import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

export type SupabaseAuthStorage = {
  getItem: (key: string) => string | null | Promise<string | null>
  setItem: (key: string, value: string) => void | Promise<void>
  removeItem: (key: string) => void | Promise<void>
}

function readLocalStorage(key: string) {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocalStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Native Preferences remains the source of truth when localStorage is unavailable.
  }
}

function removeLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Ignore storage access errors; Supabase will still remove native storage below.
  }
}

const browserAuthStorage: SupabaseAuthStorage = {
  getItem: (key) => readLocalStorage(key),
  setItem: (key, value) => writeLocalStorage(key, value),
  removeItem: (key) => removeLocalStorage(key),
}

const nativePreferencesAuthStorage: SupabaseAuthStorage = {
  async getItem(key) {
    const { value } = await Preferences.get({ key })
    if (value !== null) {
      return value
    }

    const legacyValue = readLocalStorage(key)
    if (legacyValue !== null) {
      await Preferences.set({ key, value: legacyValue })
    }

    return legacyValue
  },
  async setItem(key, value) {
    await Preferences.set({ key, value })
    writeLocalStorage(key, value)
  },
  async removeItem(key) {
    await Preferences.remove({ key })
    removeLocalStorage(key)
  },
}

export function createSupabaseAuthStorage(): SupabaseAuthStorage | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  if (Capacitor.isNativePlatform()) {
    return nativePreferencesAuthStorage
  }

  return browserAuthStorage
}
